import { Loan, LiquidityEvent } from '../types'

/**
 * Calculate the monthly payment for a loan based on its repayment type.
 */
export function calculateMonthlyPayment(loan: Loan): number {
  const principal = Math.max(0, loan.totalAmount - loan.depositAmount)
  if (principal === 0 || loan.durationMonths === 0) return 0

  const monthlyRate = loan.interestRate / 100 / 12
  const n = loan.durationMonths

  if (monthlyRate === 0) {
    return principal / n
  }

  switch (loan.repaymentType) {
    case 'annuity':
      // M = P * [r(1+r)^n] / [(1+r)^n - 1]
      return (
        (principal * (monthlyRate * Math.pow(1 + monthlyRate, n))) /
        (Math.pow(1 + monthlyRate, n) - 1)
      )

    case 'interest-only':
      // Just the interest each month
      return principal * monthlyRate

    case 'linear':
      // Average payment: principal portion + average interest portion
      // First month interest: P * r
      // Last month interest: (P/n) * r
      // Average interest: (P * r + P/n * r) / 2 ≈ P * r / 2
      const principalPortion = principal / n
      const averageInterest = (principal * monthlyRate) / 2
      return principalPortion + averageInterest

    default:
      return 0
  }
}

/**
 * Calculate the total interest paid over the life of the loan.
 */
export function calculateTotalInterest(loan: Loan): number {
  const principal = Math.max(0, loan.totalAmount - loan.depositAmount)
  if (principal === 0 || loan.durationMonths === 0) return 0

  const monthlyPayment = calculateMonthlyPayment(loan)

  if (loan.repaymentType === 'interest-only') {
    return monthlyPayment * loan.durationMonths
  }

  // For annuity and linear, total paid minus principal is the interest
  const totalPaid = monthlyPayment * loan.durationMonths
  return Math.max(0, totalPaid - principal)
}

/**
 * Convert active loans into LiquidityEvents for the projection engine.
 * Generates:
 *  - Monthly repayment events (negative outflow)
 *  - One-time deposit/down-payment events (negative outflow, cash out at loan start)
 */
export function loansToLiquidityEvents(loans: Loan[]): LiquidityEvent[] {
  const events: LiquidityEvent[] = []

  loans
    .filter((loan) => loan.isActive)
    .forEach((loan) => {
      // Monthly repayment event
      const monthlyPayment = calculateMonthlyPayment(loan)
      const durationYears = Math.ceil(loan.durationMonths / 12)

      events.push({
        id: `loan-${loan.id}`,
        name: `${loan.name} Repayment`,
        amount: -monthlyPayment,
        currency: loan.currency,
        type: 'monthly',
        startYear: loan.startYear,
        endYear: loan.startYear + durationYears - 1,
      })

      // One-time deposit/down-payment outflow (if any)
      // Skip if a funding source was selected — handleAddLoan already created a tracked cash event
      if (loan.depositAmount > 0 && !loan.depositFundedFromHoldingId) {
        events.push({
          id: `loan-deposit-${loan.id}`,
          name: `${loan.name} Deposit`,
          amount: -loan.depositAmount,
          currency: loan.currency,
          type: 'one-time',
          startYear: loan.startYear,
        })
      }
    })

  return events
}
